import { withListOverflow } from "../../v2/components/ListOverflow.jsx";
export const AdminIntegrationsRegistry = withListOverflow(IntegrationsListOwner);
// ADMIN-INTEGRATIONS-REGISTRY
//
// Admin-editable metadata registry. Credential fields are key names only:
// this component never reads environment variables or Replit Secrets.

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { T } from "../../app/tokens.js";
import { ErrorBanner } from "../../components/ErrorBanner.jsx";
import {
  createIntegration,
  deleteIntegration,
  getEnvoyAttendanceFeedHealth,
  getEnvoyAttendanceBackfillStatus,
  getIntegrationRegistry,
  startEnvoyAttendanceBackfillRun,
  updateIntegration,
  syncPlacesUsers,
} from "../../data/adminClient.js";
import { AttendanceFeedHealthReport } from "./AttendanceFeedHealthReport.jsx";

const STATUSES = ["live", "configured", "planned", "legacy"];
const STATUS_LABELS = {
  live: "Live",
  configured: "Configured",
  planned: "Planned",
  legacy: "Legacy",
};
const STATUS_DESCRIPTIONS = {
  live: "flowing to production now",
  configured: "set up + verified, not yet feeding the app",
  planned: "not set up",
  legacy: "retired/retained",
};
const STATUS_STYLE = {
  live: { background: T.ragGreenBg, color: T.ragGreenText },
  configured: { background: T.blueSoft, color: T.blue },
  planned: { background: T.lightGray, color: T.mid },
  legacy: { background: T.ragAmberBg, color: T.ragAmberText },
};

const AUTH_METHOD_OPTIONS = [
  ["oauth2_client_credentials", "OAuth2 (client-credentials)"],
  ["oauth2_auth_code", "OAuth2 (authorization code)"],
  ["oidc_sso", "OIDC SSO"],
  ["api_key", "API key"],
  ["webhook", "Webhook"],
  ["file_export", "File export"],
  ["none", "None"],
];
const CADENCE_OPTIONS = [
  ["continuous", "Continuous"],
  ["hourly", "Hourly"],
  ["daily", "Daily"],
  ["weekly", "Weekly"],
  ["monthly", "Monthly"],
  ["on_demand", "On demand"],
  ["per_session", "Per session"],
  ["per_event", "Per event"],
];
const DIRECTION_OPTIONS = [
  ["inbound_read", "Inbound (read)"],
  ["inbound_readwrite", "Inbound (read/write)"],
  ["outbound", "Outbound"],
  ["bidirectional", "Bidirectional"],
  ["auth", "Authentication"],
];
const AUTH_METHOD_LABELS = Object.fromEntries(AUTH_METHOD_OPTIONS);
const CADENCE_LABELS = Object.fromEntries(CADENCE_OPTIONS);
const DIRECTION_LABELS = Object.fromEntries(DIRECTION_OPTIONS);

const CROSSWALK_SOURCE_BY_INTEGRATION = {
  envoy: "envoy",
  workday: "workday",
  spacebase: "spacebase",
};

const EMPTY_FORM = {
  name: "",
  status: "planned",
  dataFlow: "",
  direction: "",
  authMethod: "",
  credentialKeys: "",
  scopes: "",
  cadence: "",
  docsUrl: "",
  owner: "",
  notes: "",
  sortOrder: "0",
};

const FIELD_DEFS = [
  ["name", "Integration name", "text"],
  ["owner", "Owner", "text"],
  ["dataFlow", "Data and flow", "text"],
  ["scopes", "Scopes / details", "text"],
  ["docsUrl", "Documentation URL", "url"],
  ["sortOrder", "Sort order", "number"],
];

const TITLE = {
  margin: "0 0 5px",
  color: T.dark,
  fontSize: T.fsLg5,
  fontWeight: T.fwSemibold,
};
const BUTTON_PRIMARY = {
  border: "none",
  borderRadius: 6,
  padding: "7px 13px",
  background: T.blue,
  color: T.white,
  fontFamily: "inherit",
  fontSize: T.fsSm,
  fontWeight: T.fwBold,
  cursor: "pointer",
};
const BUTTON_SECONDARY = {
  border: `1px solid ${T.border}`,
  borderRadius: 6,
  padding: "7px 13px",
  background: T.white,
  color: T.dark,
  fontFamily: "inherit",
  fontSize: T.fsSm,
  fontWeight: T.fwSemibold,
  cursor: "pointer",
};
const FIELD_STYLE = {
  width: "100%",
  boxSizing: "border-box",
  padding: "7px 8px",
  border: `1px solid ${T.border}`,
  borderRadius: 5,
  background: T.white,
  color: T.dark,
  fontFamily: "inherit",
  fontSize: T.fsSm,
};
const LABEL_STYLE = {
  display: "block",
  marginBottom: 4,
  color: T.mid,
  fontSize: T.fsXs,
  fontWeight: T.fwSemibold,
};

function emptyToNull(value) {
  const clean = String(value ?? "").trim();
  return clean || null;
}

function friendlyLabel(labels, value) {
  return labels[value] ?? value;
}

function relativeTime(value) {
  if (!value) return "never";
  const delta = Date.now() - new Date(value).getTime();
  const future = delta < 0;
  const minutes = Math.max(1, Math.round(Math.abs(delta) / 60_000));
  if (minutes < 60) return future ? `in ${minutes}m` : `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return future ? `in ${hours}h` : `${hours}h ago`;
  const days = Math.round(hours / 24);
  return future ? `in ${days}d` : `${days}d ago`;
}

function absoluteTime(value) {
  if (!value) return null;
  return new Date(value).toLocaleString();
}

function crosswalkSourceForIntegration(name) {
  return CROSSWALK_SOURCE_BY_INTEGRATION[String(name ?? "").trim().toLowerCase()] ?? null;
}

function formFromRow(row) {
  return {
    name: row.name ?? "",
    status: row.status ?? "planned",
    dataFlow: row.dataFlow ?? "",
    direction: row.direction ?? "",
    authMethod: row.authMethod ?? "",
    credentialKeys: (row.credentialKeys ?? []).join("\n"),
    scopes: row.scopes ?? "",
    cadence: row.cadence ?? "",
    docsUrl: row.docsUrl ?? "",
    owner: row.owner ?? "",
    notes: row.notes ?? "",
    sortOrder: String(row.sortOrder ?? 0),
  };
}

function toPayload(form) {
  const credentialKeys = form.credentialKeys
    .split(/[\s,]+/)
    .map((key) => key.trim())
    .filter(Boolean);
  return {
    name: form.name.trim(),
    status: form.status,
    dataFlow: emptyToNull(form.dataFlow),
    direction: emptyToNull(form.direction),
    authMethod: emptyToNull(form.authMethod),
    credentialKeys,
    scopes: emptyToNull(form.scopes),
    cadence: emptyToNull(form.cadence),
    docsUrl: emptyToNull(form.docsUrl),
    owner: emptyToNull(form.owner),
    notes: emptyToNull(form.notes),
    sortOrder: Number(form.sortOrder || 0),
  };
}

function StatusChip({ status }) {
  const style = STATUS_STYLE[status] ?? STATUS_STYLE.planned;
  return (
    <span
      data-testid={`integration-status-${status}`}
      style={{
        ...style,
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        borderRadius: 999,
        padding: "3px 8px",
        fontSize: T.fsXxxs5,
        fontWeight: T.fwBold,
        whiteSpace: "nowrap",
      }}
    >
      <span style={{ width: 7, height: 7, borderRadius: "50%", background: style.color }} />
      {STATUS_LABELS[status] ?? status}
    </span>
  );
}

function KeyChips({ keys }) {
  if (!keys?.length) {
    return <span style={{ color: T.light, fontStyle: "italic" }}>None — manual or public</span>;
  }
  return (
    <div>
      {keys.map((key) => (
        <span
          key={key}
          data-testid={`integration-key-${key}`}
          style={{
            display: "inline-block",
            margin: "1px 4px 2px 0",
            padding: "2px 5px",
            border: `1px solid ${T.border}`,
            borderRadius: 4,
            background: T.lightGray,
            color: T.dark,
            fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
            fontSize: T.fsXxxs5,
          }}
        >
          {key}
        </span>
      ))}
    </div>
  );
}

function RefreshStatus({ runtime, rowId }) {
  const state = runtime ?? {
    mode: "manual",
    automated: false,
    cadenceLabel: "On upload / manual",
    status: "manual",
  };
  const isWarning = state.stale || state.status === "failed";
  const accent = state.staleSeverity === "red" || state.status === "failed"
    ? T.ragRed
    : T.ragAmberText;
  const background = state.staleSeverity === "red" || state.status === "failed"
    ? T.ragRedBg
    : T.ragAmberBg;
  const statusLabel = state.status === "success"
    ? "Success"
    : state.status === "failed"
      ? "Failed"
      : state.status === "never"
        ? "Never run"
        : state.status === "running" ? "Running"
          : state.status === "unavailable" ? "Runtime state unavailable" : "Manual";

  return (
    <div
      data-testid={`integration-refresh-${rowId}`}
      style={{
        minWidth: 190,
        padding: "6px 8px",
        borderRadius: 6,
        background: isWarning ? background : "transparent",
        color: isWarning ? accent : T.mid,
      }}
    >
      <strong style={{ display: "block", color: isWarning ? accent : T.dark, fontSize: T.fsXs }}>
        {state.cadenceLabel ?? (state.automated ? "Automated" : "On upload / manual")}
      </strong>
      <span style={{ display: "block", marginTop: 2, fontSize: T.fsXs }}>
        {state.automated || state.scheduleEnabled === false ? statusLabel : "Manual refresh"}
        {state.lastRunAt && (
          <>
            {state.scheduleEnabled === false ? " · Last attempt: " : " · "}
            <time dateTime={state.lastRunAt} title={absoluteTime(state.lastRunAt)}>
              {relativeTime(state.lastRunAt)}
            </time>
          </>
        )}
      </span>
      {state.lastSuccessAt && (state.mode === "activity" || state.scheduleEnabled === false || state.lastSuccessAt !== state.lastRunAt) && (
        <span style={{ display: "block", marginTop: 2, fontSize: T.fsXxs, color: T.mid }}>
          {state.mode === "activity" ? "Last synced " : "Last success: "}
          <time dateTime={state.lastSuccessAt} title={absoluteTime(state.lastSuccessAt)}>
            {relativeTime(state.lastSuccessAt)}
          </time>
          {state.mode === "activity" && (state.lastSuccessTrigger ? ` · ${state.lastSuccessTrigger}` : " · source unknown")}
        </span>
      )}
      {state.nextRunAt && (
        <span style={{ display: "block", marginTop: 2, fontSize: T.fsXxs, color: T.mid }}>
          Next:{" "}
          <time dateTime={state.nextRunAt} title={absoluteTime(state.nextRunAt)}>
            {relativeTime(state.nextRunAt)}
          </time>
        </span>
      )}
      {(state.scheduleEnabled === false || state.mode === "activity") && (
        <div style={{ marginTop: 3, fontSize: T.fsXxs }}>
          {state.mode === "activity" ? (
            !state.lastSuccessAt && <div>No successful sync recorded</div>
          ) : <div>Next run: not scheduled</div>}
          {state.counts ? (
            <>
              <div>Last successful counts</div>
              <div>Matched: {state.counts.matched} · Added: {state.counts.added}</div>
              <div>Reactivated: {state.counts.reactivated} · Deactivation candidates: {state.counts.deactivationCandidates}</div>
            </>
          ) : <div>No successful run counts available</div>}
          <div>{state.candidatePopulation}. Report only; no automatic deactivation.</div>
        </div>
      )}
      {isWarning && (
        <span data-testid={`integration-refresh-warning-${rowId}`} style={{ display: "block", marginTop: 3, fontSize: T.fsXxs, fontWeight: T.fwSemibold }}>
          {state.status === "failed" ? (state.error ?? "Last run failed") : "Refresh is stale"}
        </span>
      )}
    </div>
  );
}

function IntegrationForm({ initial, saving, onCancel, onSave }) {
  const [form, setForm] = useState(() => initial ? formFromRow(initial) : EMPTY_FORM);
  const [formError, setFormError] = useState(null);

  function setField(field, value) {
    setForm((current) => ({ ...current, [field]: value }));
    setFormError(null);
  }

  async function submit(event) {
    event.preventDefault();
    if (!form.name.trim()) {
      setFormError(new Error("Integration name is required."));
      return;
    }
    try {
      await onSave(toPayload(form));
    } catch (err) {
      setFormError(err);
    }
  }

  return (
    <form
      data-testid="integration-form"
      onSubmit={submit}
      style={{
        marginBottom: 18,
        padding: 16,
        border: `1px solid ${T.border}`,
        borderRadius: 7,
        background: T.hoverBg,
      }}
    >
      <h3 style={{ ...TITLE, fontSize: T.fsBase, marginBottom: 14 }}>
        {initial ? "Edit integration" : "Add integration"}
      </h3>
      {formError && <ErrorBanner error={formError} onDismiss={() => setFormError(null)} />}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(150px, 1fr))", gap: 12 }}>
        {FIELD_DEFS.map(([field, label, type]) => (
          <label key={field} style={{ minWidth: 0 }}>
            <span style={LABEL_STYLE}>{label}{field === "name" ? " *" : ""}</span>
            <input
              data-testid={`integration-field-${field}`}
              type={type}
              value={form[field]}
              onChange={(event) => setField(field, event.target.value)}
              style={FIELD_STYLE}
              required={field === "name"}
              min={type === "number" ? 0 : undefined}
            />
          </label>
        ))}
        {[
          ["direction", "Direction", DIRECTION_OPTIONS],
          ["authMethod", "Authentication", AUTH_METHOD_OPTIONS],
          ["cadence", "Cadence", CADENCE_OPTIONS],
        ].map(([field, label, options]) => (
          <label key={field} style={{ minWidth: 0 }}>
            <span style={LABEL_STYLE}>{label}</span>
            <select
              data-testid={`integration-field-${field}`}
              value={form[field]}
              onChange={(event) => setField(field, event.target.value)}
              style={FIELD_STYLE}
            >
              <option value="">— Not set —</option>
              {options.map(([value, optionLabel]) => (
                <option key={value} value={value}>{optionLabel}</option>
              ))}
            </select>
          </label>
        ))}
        <label>
          <span style={LABEL_STYLE}>Status *</span>
          <select
            data-testid="integration-field-status"
            value={form.status}
            onChange={(event) => setField("status", event.target.value)}
            style={FIELD_STYLE}
          >
            {STATUSES.map((status) => <option key={status} value={status}>{STATUS_LABELS[status]}</option>)}
          </select>
        </label>
        <label style={{ gridColumn: "span 2" }}>
          <span style={LABEL_STYLE}>Credential key names</span>
          <textarea
            data-testid="integration-field-credentialKeys"
            value={form.credentialKeys}
            onChange={(event) => setField("credentialKeys", event.target.value)}
            placeholder="ONE_KEY_NAME per line"
            rows={3}
            style={{ ...FIELD_STYLE, resize: "vertical" }}
          />
          <span style={{ display: "block", marginTop: 3, color: T.mid, fontSize: T.fsXs }}>
            Key names only. Never enter or paste secret values.
          </span>
        </label>
        <label style={{ gridColumn: "1 / -1" }}>
          <span style={LABEL_STYLE}>Notes</span>
          <textarea
            data-testid="integration-field-notes"
            value={form.notes}
            onChange={(event) => setField("notes", event.target.value)}
            rows={2}
            style={{ ...FIELD_STYLE, resize: "vertical" }}
          />
        </label>
      </div>
      <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
        <button type="submit" data-testid="integration-save-btn" disabled={saving} style={{ ...BUTTON_PRIMARY, opacity: saving ? 0.6 : 1 }}>
          {saving ? "Saving…" : "Save integration"}
        </button>
        <button type="button" data-testid="integration-cancel-btn" onClick={onCancel} style={BUTTON_SECONDARY}>
          Cancel
        </button>
      </div>
    </form>
  );
}

function IntegrationsListOwner({ currentRole, explainer } = {}) {
  const isAdmin = currentRole === "admin";
  const [showFeedHealth, setShowFeedHealth] = useState(() => {
    if (typeof window === "undefined") return false;
    return new URLSearchParams(window.location.search).get("feedHealth") === "envoy";
  });
  const [feedHealth, setFeedHealth] = useState(null);
  const [feedHealthLoading, setFeedHealthLoading] = useState(false);
  const [feedHealthError, setFeedHealthError] = useState(null);
  const [integrations, setIntegrations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteText, setDeleteText] = useState("");
  const [deleteError, setDeleteError] = useState(null);
  const [envoyRun, setEnvoyRun] = useState(null);
  const [envoyRunError, setEnvoyRunError] = useState(null);
  const [startingEnvoyRun, setStartingEnvoyRun] = useState(false);
  const placesPending = useRef(false);
  const [placesSyncing, setPlacesSyncing] = useState(false);
  const [placesError, setPlacesError] = useState(null);
  const [placesWarning, setPlacesWarning] = useState(null);

  async function syncPlaces() {
    if (placesPending.current) return;
    placesPending.current = true;
    setPlacesSyncing(true);
    setPlacesError(null);
    setPlacesWarning(null);
    try {
      const result = await syncPlacesUsers();
      setPlacesWarning(result.warning ?? null);
    } catch (err) {
      setPlacesError(err);
    } finally {
      // Refresh failed attempts too, preserving last-good values if GET fails.
      try {
        const response = await getIntegrationRegistry();
        setIntegrations(response.integrations);
      } catch (err) {
        setPlacesWarning(`Runtime refresh failed: ${err.message}`);
      }
      placesPending.current = false;
      setPlacesSyncing(false);
    }
  }

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await getIntegrationRegistry();
      setIntegrations(response.integrations ?? []);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const loadEnvoyRun = useCallback(async () => {
    try {
      const response = await getEnvoyAttendanceBackfillStatus();
      setEnvoyRun(response.run ?? null);
      setEnvoyRunError(null);
    } catch (err) {
      setEnvoyRunError(err);
    }
  }, []);

  useEffect(() => {
    loadEnvoyRun();
  }, [loadEnvoyRun]);

  useEffect(() => {
    if (!isAdmin || !showFeedHealth) return undefined;
    let current = true;
    setFeedHealthLoading(true);
    setFeedHealthError(null);
    getEnvoyAttendanceFeedHealth()
      .then((response) => {
        if (current) setFeedHealth(response);
      })
      .catch((err) => {
        if (current) setFeedHealthError(err);
      })
      .finally(() => {
        if (current) setFeedHealthLoading(false);
      });
    return () => {
      current = false;
    };
  }, [isAdmin, showFeedHealth]);

  useEffect(() => {
    if (!envoyRun || !["pending", "running"].includes(envoyRun.status)) return undefined;
    const timer = window.setInterval(loadEnvoyRun, 5000);
    return () => window.clearInterval(timer);
  }, [envoyRun, loadEnvoyRun]);

  const sortedIntegrations = useMemo(
    () => [...integrations].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || a.name.localeCompare(b.name)),
    [integrations],
  );

  async function save(payload) {
    setSaving(true);
    try {
      const response = editing?.id
        ? await updateIntegration(editing.id, payload)
        : await createIntegration(payload);
      const saved = response.integration;
      setIntegrations((current) => editing?.id
        ? current.map((row) => row.id === saved.id ? saved : row)
        : [...current, saved]);
      setEditing(null);
    } finally {
      setSaving(false);
    }
  }

  async function confirmDelete() {
    if (!deleteTarget || deleteText !== deleteTarget.name) return;
    setDeleteError(null);
    try {
      await deleteIntegration(deleteTarget.id);
      setIntegrations((current) => current.filter((row) => row.id !== deleteTarget.id));
      setDeleteTarget(null);
      setDeleteText("");
    } catch (err) {
      setDeleteError(err);
    }
  }

  async function startEnvoyRun() {
    setStartingEnvoyRun(true);
    setEnvoyRunError(null);
    try {
      const response = await startEnvoyAttendanceBackfillRun({ scope: "full_year" });
      setEnvoyRun(response.run ?? null);
      await loadEnvoyRun();
    } catch (err) {
      setEnvoyRunError(err);
    } finally {
      setStartingEnvoyRun(false);
    }
  }

  function envoyStatusLabel() {
    if (!envoyRun) return "No run started";
    if (envoyRun.status === "completed") return "Completed";
    if (envoyRun.status === "failed") return "Failed";
    if (envoyRun.status === "running") return "Running";
    return "Queued";
  }

  const envoyRunActive = envoyRun?.status === "pending"
    || (envoyRun?.status === "running" && !envoyRun.stale);
  const envoyRemaining = envoyRun
    && ["pending", "running"].includes(envoyRun.status)
    && Number(envoyRun.totalOffices ?? 0) > Number(envoyRun.completedOffices ?? 0)
    && Number(envoyRun.officesRemaining ?? 0) === 0
    ? Number(envoyRun.totalOffices) - Number(envoyRun.completedOffices ?? 0)
    : envoyRun?.officesRemaining ?? 0;

  function returnToIntegrations() {
    setShowFeedHealth(false);
    if (typeof window !== "undefined") {
      const url = new URL(window.location.href);
      url.searchParams.delete("feedHealth");
      window.history.replaceState(window.history.state, "", url);
    }
  }

  if (isAdmin && showFeedHealth) {
    return (
      <AttendanceFeedHealthReport
        report={feedHealth}
        loading={feedHealthLoading}
        error={feedHealthError}
        onBack={returnToIntegrations}
      />
    );
  }

  return (
    <div data-testid="admin-integrations-registry">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16 }}>
        <div>
          <h1 style={TITLE}>Integrations</h1>
        </div>
        {isAdmin && (
          <button
            type="button"
            data-testid="add-integration-btn"
            onClick={() => setEditing({ ...EMPTY_FORM, id: null })}
            style={{ ...BUTTON_PRIMARY, whiteSpace: "nowrap" }}
          >
            + Add integration
          </button>
        )}
      </div>
      {explainer}

      <div
        data-testid="integrations-status-legend"
        aria-label="Integration status legend"
        style={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: "7px 14px",
          marginBottom: 16,
          padding: "8px 10px",
          border: `1px solid ${T.border}`,
          borderRadius: 7,
          background: T.card,
          color: T.mid,
          fontSize: T.fsXs,
          lineHeight: 1.4,
        }}
      >
        <strong style={{ color: T.dark, fontSize: T.fsXs }}>Status:</strong>
        {STATUSES.map((status) => {
          const style = STATUS_STYLE[status];
          return (
            <span
              key={status}
              data-testid={`integration-status-legend-${status}`}
              style={{ display: "inline-flex", alignItems: "center", gap: 5 }}
            >
              <span
                aria-hidden="true"
                style={{ width: 7, height: 7, borderRadius: "50%", background: style.color, flex: "0 0 auto" }}
              />
              <strong style={{ color: style.color }}>{STATUS_LABELS[status]}</strong>
              <span>{STATUS_DESCRIPTIONS[status]}</span>
            </span>
          );
        })}
      </div>

      <section
        data-testid="envoy-backfill-status"
        style={{ marginBottom: 16, padding: 14, border: `1px solid ${T.border}`, borderRadius: 7, background: T.card }}
      >
        <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start" }}>
          <div>
            <strong style={{ color: T.dark }}>Envoy attendance backfill</strong>
            <div style={{ marginTop: 4, color: T.mid, fontSize: T.fsSm }}>
              {envoyStatusLabel()}
              {envoyRun?.campaign?.scope === "full_year"
                && ` · ${envoyRun.campaign.completedWindows ?? 0}/${envoyRun.campaign.totalWindows ?? 0} windows covered`}
              {envoyRun && ` · ${envoyRun.completedOffices ?? 0}/${envoyRun.totalOffices ?? 0} offices completed`}
              {envoyRun && ` · ${envoyRemaining} remaining`}
            </div>
            {envoyRun?.roster && (
              <div data-testid="envoy-backfill-roster-progress" style={{ marginTop: 4, color: T.mid, fontSize: T.fsXs }}>
                Roster: {envoyRun.roster.pages ?? 0} pages
                {envoyRun.roster.nextPage == null
                  ? " · complete"
                  : ` · next page ${envoyRun.roster.nextPage}`}
                {envoyRun.roster.total != null && ` of ${envoyRun.roster.total}`}
              </div>
            )}
            {envoyRun?.lastErrorCode && (
              <div data-testid="envoy-backfill-failure" style={{ marginTop: 5, color: T.ragAmberText, fontSize: T.fsSm }}>
                Last failure: {envoyRun.lastErrorCode}
                {envoyRun.lastErrorPage != null && ` on roster page ${envoyRun.lastErrorPage}`}
                {envoyRun.lastErrorLayer && ` (${envoyRun.lastErrorLayer})`}
              </div>
            )}
            {envoyRun?.campaign?.scope === "full_year" && envoyRun.campaign.oldestCoveredDate && (
              <div data-testid="envoy-backfill-oldest-covered" style={{ marginTop: 3, color: T.light, fontSize: T.fsXs }}>
                Oldest covered date: {envoyRun.campaign.oldestCoveredDate}
              </div>
            )}
            {envoyRun?.finishedAt && (
              <div style={{ marginTop: 3, color: T.light, fontSize: T.fsXs }}>
                Last run: {envoyRun.finishedAt}
              </div>
            )}
            {envoyRunError && (
              <div style={{ marginTop: 5, color: T.red, fontSize: T.fsSm }}>
                Unable to load backfill status.
              </div>
            )}
          </div>
          {isAdmin && (
            <button
              type="button"
              data-testid="envoy-backfill-run-btn"
              onClick={startEnvoyRun}
              disabled={startingEnvoyRun || envoyRunActive}
              style={{ ...BUTTON_PRIMARY, whiteSpace: "nowrap", opacity: startingEnvoyRun ? 0.6 : 1 }}
            >
              {startingEnvoyRun ? "Starting…" : envoyRun?.status === "failed" || envoyRun?.stale ? "Resume run" : "Run backfill"}
            </button>
          )}
        </div>
      </section>

      <div
        data-testid="integrations-no-secrets-notice"
        style={{
          display: "flex",
          gap: 9,
          alignItems: "flex-start",
          marginBottom: 16,
          padding: "10px 12px",
          border: `1px solid ${T.border}`,
          borderRadius: 7,
          background: T.hoverBg,
          color: T.mid,
          fontSize: T.fsSm,
          lineHeight: 1.5,
        }}
      >
        <span aria-hidden="true" style={{ color: T.blue, fontSize: T.fsBase }}>▣</span>
        <span><strong style={{ color: T.dark }}>No secret values are stored or shown here.</strong> This registry references credential <strong style={{ color: T.dark }}>key names</strong> only. Secret values remain server-side in Replit Secrets.</span>
      </div>

      {editing && isAdmin && (
        <IntegrationForm
          initial={editing.id ? editing : null}
          saving={saving}
          onCancel={() => setEditing(null)}
          onSave={save}
        />
      )}

      {error && <ErrorBanner error={error} onDismiss={() => setError(null)} />}
      {loading ? (
        <div data-testid="integrations-loading" style={{ padding: "34px 0", color: T.mid, textAlign: "center" }}>Loading integrations…</div>
      ) : sortedIntegrations.length === 0 ? (
        <div data-testid="integrations-empty" style={{ padding: "34px 0", color: T.mid, textAlign: "center" }}>No integrations have been registered.</div>
      ) : (
        <div style={{ overflowX: "auto", border: `1px solid ${T.border}`, borderRadius: 7 }}>
          <table data-testid="integrations-table" style={{ width: "100%", borderCollapse: "collapse", minWidth: 1080 }}>
            <thead>
              <tr>
                {["Integration", "Status", "Data & flow", "Auth", "Credential keys", "Cadence", "Refresh", "Docs", ...(isAdmin ? ["Actions"] : [])].map((heading) => (
                  <th key={heading} style={{ padding: "9px 10px", borderBottom: `1px solid ${T.border}`, background: T.tableHeadBg, color: T.mid, fontSize: T.fsXxxs5, fontWeight: T.fwBold, letterSpacing: 0.5, textAlign: "left", textTransform: "uppercase", whiteSpace: "nowrap" }}>
                    {heading}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sortedIntegrations.map((row) => (
                <tr key={row.id} data-testid={`integration-row-${row.id}`}>
                  <td style={{ ...cellStyle, minWidth: 130 }}>
                    <strong style={{ color: T.dark, fontSize: T.fsSm5 }}>{row.name}</strong>
                    {row.owner && <span style={{ display: "block", marginTop: 2, color: T.light, fontSize: T.fsXs }}>{row.owner}</span>}
                    {crosswalkSourceForIntegration(row.name) && (
                      <>
                        <a
                          data-testid={`integration-location-mappings-${row.id}`}
                          href={`/admin?section=markets_location_ids&tab=external-ids&source=${crosswalkSourceForIntegration(row.name)}`}
                          style={{ display: "block", marginTop: 5, color: T.blue, fontSize: T.fsXs, fontWeight: T.fwSemibold, whiteSpace: "nowrap" }}
                        >
                          View location mappings →
                        </a>
                        {isAdmin && String(row.name).trim().toLowerCase() === "envoy" && (
                          <a
                            data-testid={`integration-feed-health-${row.id}`}
                            href="/admin?section=integrations&feedHealth=envoy"
                            style={{ display: "block", marginTop: 4, color: T.blue, fontSize: T.fsXs, fontWeight: T.fwSemibold, whiteSpace: "nowrap" }}
                          >
                            View Feed Health →
                          </a>
                        )}
                      </>
                    )}
                  </td>
                  <td style={cellStyle}><StatusChip status={row.status} /></td>
                  <td style={cellStyle}>
                    <span>{row.dataFlow || <span style={mutedStyle}>—</span>}</span>
                    {row.direction && <span style={{ display: "block", marginTop: 2, color: T.light, fontSize: T.fsXxxs5, fontWeight: T.fwBold, letterSpacing: 0.4 }}>{friendlyLabel(DIRECTION_LABELS, row.direction)}</span>}
                    {row.scopes && <span style={{ display: "block", marginTop: 2, color: T.mid, fontSize: T.fsXs }}>scopes: {row.scopes}</span>}
                  </td>
                  <td style={cellStyle}>{row.authMethod ? friendlyLabel(AUTH_METHOD_LABELS, row.authMethod) : <span style={mutedStyle}>—</span>}</td>
                  <td style={{ ...cellStyle, minWidth: 180 }}><KeyChips keys={row.credentialKeys} /></td>
                  <td style={cellStyle}>{row.cadence ? friendlyLabel(CADENCE_LABELS, row.cadence) : <span style={mutedStyle}>—</span>}</td>
                  <td style={cellStyle}>
                    <RefreshStatus runtime={row.runtime} rowId={row.id} />
                    {String(row.name).trim().toLowerCase() === "places users" && (
                      <>
                        {isAdmin && <button type="button" onClick={syncPlaces} disabled={placesSyncing || row.runtime?.status === "running"} style={BUTTON_SECONDARY}>{placesSyncing ? "Syncing…" : "Sync now"}</button>}
                        {placesError && <ErrorBanner error={placesError} />}
                        {placesWarning && <div role="status" style={{ color: T.ragAmberText, fontSize: T.fsXs }}>{placesWarning}</div>}
                      </>
                    )}
                  </td>
                  <td style={cellStyle}>
                    {row.docsUrl ? <a href={row.docsUrl} target="_blank" rel="noreferrer" style={{ color: T.blue, fontWeight: T.fwBold }}>Open →</a> : <span style={mutedStyle}>—</span>}
                  </td>
                  {isAdmin && (
                    <td style={{ ...cellStyle, whiteSpace: "nowrap" }}>
                      <button type="button" data-testid={`edit-integration-${row.id}`} onClick={() => setEditing(row)} style={{ ...BUTTON_SECONDARY, padding: "5px 8px", marginRight: 5 }}>Edit</button>
                      <button type="button" data-testid={`delete-integration-${row.id}`} onClick={() => { setDeleteTarget(row); setDeleteText(""); setDeleteError(null); }} style={{ ...BUTTON_SECONDARY, color: T.red, padding: "5px 8px" }}>Delete</button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {deleteTarget && (
        <div
          data-testid="integration-delete-confirm"
          role="dialog"
          aria-modal="true"
          style={{ marginTop: 16, padding: 14, border: `1px solid ${T.ragAmberBorder}`, borderRadius: 7, background: T.ragAmberBg }}
        >
          <strong style={{ display: "block", color: T.dark, marginBottom: 5 }}>Delete {deleteTarget.name}?</strong>
          <span style={{ display: "block", color: T.mid, fontSize: T.fsSm, lineHeight: 1.4 }}>
            This permanently removes this registry row and records the deletion in the audit log. Type <strong style={{ color: T.dark }}>{deleteTarget.name}</strong> to confirm.
          </span>
          {deleteError && <div style={{ marginTop: 8 }}><ErrorBanner error={deleteError} onDismiss={() => setDeleteError(null)} /></div>}
          <input
            data-testid="integration-delete-confirm-input"
            value={deleteText}
            onChange={(event) => setDeleteText(event.target.value)}
            aria-label={`Type ${deleteTarget.name} to confirm`}
            style={{ ...FIELD_STYLE, maxWidth: 320, marginTop: 10, background: T.white }}
          />
          <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
            <button type="button" data-testid="integration-delete-confirm-btn" disabled={deleteText !== deleteTarget.name} onClick={confirmDelete} style={{ ...BUTTON_PRIMARY, background: T.red, opacity: deleteText === deleteTarget.name ? 1 : 0.5 }}>Delete permanently</button>
            <button type="button" data-testid="integration-delete-cancel-btn" onClick={() => setDeleteTarget(null)} style={BUTTON_SECONDARY}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

const cellStyle = {
  padding: "10px",
  borderBottom: `1px solid ${T.border}`,
  color: T.dark,
  fontSize: T.fsSm,
  lineHeight: 1.45,
  verticalAlign: "top",
};
const mutedStyle = { color: T.light, fontStyle: "italic" };
